import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSession, updateSession, getActivities, getLevels, getFiches, createFiche, updateFiche, deleteFiche } from '../utils/api';
import { generateFichePDF } from '../utils/generateFichePDF';
import { useToast } from './ToastProvider';
import Icon from './Icon';
import FichePalanquierModal from './FichePalanquierModal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../styles/SessionPlanning.css';

function SessionPlanning() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [session, setSession] = useState(null);
  const [activities, setActivities] = useState({ theoretical: [], practical: [] });
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [dayFiches, setDayFiches] = useState([]);
  const [editingFiche, setEditingFiche] = useState(null); // null = nouvelle, objet = existante
  const [ficheModalOpen, setFicheModalOpen] = useState(false);
  const [copyingFiche, setCopyingFiche] = useState(null); // fiche source pour la copie
  const [copyTargetDate, setCopyTargetDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCommonActivityModal, setShowCommonActivityModal] = useState(false);
  const [selectedCommonActivities, setSelectedCommonActivities] = useState([]);
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [newTimeSlotInput, setNewTimeSlotInput] = useState('');
  const [editingSlotHour, setEditingSlotHour] = useState(null);
  const [editSlotInput, setEditSlotInput] = useState('');
  const [showBlankFicheModal, setShowBlankFicheModal] = useState(false);
  const [blankNbPal, setBlankNbPal] = useState(2);
  const [blankNbDiv, setBlankNbDiv] = useState(4);
  const [selectedRecurrenceDays, setSelectedRecurrenceDays] = useState([]);
  const [editingActivityGroup, setEditingActivityGroup] = useState(null); // { allIds, levelNames, activities, hour }
  const [editNewLevels, setEditNewLevels] = useState([]);
  const [editNewActivities, setEditNewActivities] = useState([]);
  const [publicLinkToken, setPublicLinkToken] = useState(null);
  const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);

  const DEFAULT_SLOTS = Array.from({ length: 11 }, (_, i) => i + 8); // 8h à 18h
  const timeSlots = session ? [...(session.timeSlots || DEFAULT_SLOTS)].sort((a, b) => a - b) : DEFAULT_SLOTS;

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (session && selectedDate) {
      getFiches(session.id, selectedDate)
        .then(setDayFiches)
        .catch(() => setDayFiches([]));
    }
  }, [selectedDate, session?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [sessionData, activitiesData, levelsData] = await Promise.all([
        getSession(id),
        getActivities(),
        getLevels()
      ]);
      
      setSession(sessionData);
      setActivities(activitiesData);
      setLevels(levelsData);
      
      // Sélectionner le premier jour par défaut
      const dates = getDates(sessionData.startDate, sessionData.endDate);
      if (dates.length > 0) {
        setSelectedDate(dates[0]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement de la session');
    } finally {
      setLoading(false);
    }
  };

  const getDates = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }
    
    return dates;
  };

  const saveSession = async (updatedSession) => {
    setSaving(true);
    try {
      await updateSession(id, updatedSession);
      setSession(updatedSession);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAttendanceChange = (type, personId, isPresent) => {
    const updatedSession = { ...session };
    if (!updatedSession.schedule[selectedDate].attendance) {
      updatedSession.schedule[selectedDate].attendance = { divers: {}, instructors: {}, nonDivers: {}, guidePalanquiers: {} };
    }
    if (!updatedSession.schedule[selectedDate].attendance[type]) {
      updatedSession.schedule[selectedDate].attendance[type] = {};
    }
    updatedSession.schedule[selectedDate].attendance[type][personId] = isPresent;
    saveSession(updatedSession);
  };

  const handleLevelInstructorChange = (levelId, instructorId) => {
    const updatedSession = { ...session };
    if (!updatedSession.schedule[selectedDate].attendance) {
      updatedSession.schedule[selectedDate].attendance = { divers: {}, instructors: {}, levelInstructors: {} };
    }
    if (!updatedSession.schedule[selectedDate].attendance.levelInstructors) {
      updatedSession.schedule[selectedDate].attendance.levelInstructors = {};
    }
    // Basculer dans le tableau (multi-sélection)
    const prev = updatedSession.schedule[selectedDate].attendance.levelInstructors[levelId];
    const prevArr = Array.isArray(prev) ? prev : (prev ? [prev] : []);
    const next = prevArr.includes(instructorId)
      ? prevArr.filter(id => id !== instructorId)
      : [...prevArr, instructorId];
    updatedSession.schedule[selectedDate].attendance.levelInstructors[levelId] = next;
    saveSession(updatedSession);
  };

  const handleOpenNewFiche = () => {
    setEditingFiche(null);
    setFicheModalOpen(true);
  };

  const handleOpenEditFiche = (fiche) => {
    setEditingFiche(fiche);
    setFicheModalOpen(true);
  };

  const handleSaveFiche = async (ficheData) => {
    if (editingFiche?.id) {
      const updated = await updateFiche(session.id, selectedDate, editingFiche.id, ficheData);
      setDayFiches(prev => prev.map(f => f.id === editingFiche.id ? updated : f));
      setEditingFiche(updated);
      return updated;
    } else {
      const created = await createFiche(session.id, selectedDate, ficheData);
      setDayFiches(prev => [...prev, created]);
      setEditingFiche(created);
      return created;
    }
  };

  const handleDeleteFiche = async (ficheId) => {
    await deleteFiche(session.id, selectedDate, ficheId);
    setDayFiches(prev => prev.filter(f => f.id !== ficheId));
  };

  const handleOpenCopyFiche = (fiche) => {
    setCopyingFiche(fiche);
    setCopyTargetDate(selectedDate);
  };

  const handleGenerateBlankFiche = async () => {
    try {
      const blankFormData = {
        date: '',
        heure: '',
        site: '',
        bateau: '',
        nbPlongeurs: 0,
        dp: '',
        dpBrevet: '',
        president: '',
        commentaires: '',
        hostInstructors: [],
        palanquees: Array.from({ length: blankNbPal }, (_, i) => ({
          id: i + 1,
          numero: i + 1,
          type: '',
          gaz: 'air',
          brevetPreparation: '',
          profPrevu: '', profRealise: '',
          tpsMaxPrevu: '', tpsMaxRealise: '',
          hi: '', hs: '', paliersPrevu: '', paliersRealise: '',
          plongeurs: Array.from({ length: blankNbDiv }),
        })),
      };
      await generateFichePDF(blankFormData, session);
      toast.success('Fiche vierge g\u00e9n\u00e9r\u00e9e !');
      setShowBlankFicheModal(false);
    } catch (err) {
      toast.error(err.message || 'Erreur g\u00e9n\u00e9ration PDF');
    }
  };

  const handleConfirmCopyFiche = async () => {
    if (!copyingFiche || !copyTargetDate) return;
    try {
      const { id: _id, savedAt: _sa, ...ficheData } = copyingFiche;
      // Mettre à jour la date de la fiche copiée vers le jour cible
      ficheData.date = new Date(copyTargetDate + 'T12:00:00').toLocaleDateString('fr-FR');
      const created = await createFiche(session.id, copyTargetDate, ficheData);
      if (copyTargetDate === selectedDate) {
        setDayFiches(prev => [...prev, created]);
      }
      toast.success('Fiche copiée !');
    } catch (_) {
      toast.error('Erreur lors de la copie');
    } finally {
      setCopyingFiche(null);
    }
  };

  const handleDownloadFichePDF = async (fiche) => {
    try {
      const { id: _id, savedAt: _sa, ...ficheData } = fiche;
      await generateFichePDF(ficheData, session);
      toast.success('PDF généré !');
    } catch (err) {
      toast.error(err.message || 'Erreur génération PDF');
    }
  };

  const handleActivityToggle = (activityName, activityType) => {
    if (selectedLevels.length === 0) {
      toast.error('Veuillez sélectionner au moins un niveau');
      return;
    }
    const key = `${activityType}::${activityName}`;
    setSelectedActivities(prev => {
      const exists = prev.find(a => a.key === key);
      if (exists) {
        return prev.filter(a => a.key !== key);
      }
      return [...prev, { key, name: activityName, type: activityType }];
    });
  };

  const handleConfirmActivities = () => {
    if (selectedLevels.length === 0) {
      toast.error('Veuillez sélectionner au moins un niveau');
      return;
    }
    if (selectedActivities.length === 0) {
      toast.error('Veuillez sélectionner au moins une activité');
      return;
    }

    const targetDays = selectedRecurrenceDays.length > 0 ? selectedRecurrenceDays : [selectedDate];
    const updatedSession = { ...session };

    targetDays.forEach(date => {
      if (!updatedSession.schedule[date]) {
        updatedSession.schedule[date] = { activities: [], attendance: {} };
      }
      if (!updatedSession.schedule[date].activities) {
        updatedSession.schedule[date].activities = [];
      }
      selectedLevels.forEach(levelId => {
        const level = levels.find(l => l.id === levelId);
        if (!level) return;
        selectedActivities.forEach(act => {
          updatedSession.schedule[date].activities.push({
            id: Date.now().toString() + Math.random(),
            hour: selectedTimeSlot,
            activity: act.name,
            activityType: act.type,
            level: level.name,
            activityColor: act.type === 'théorique' ? '#10b981' : '#0ea5e9',
            levelColor: level.color
          });
        });
      });
    });

    saveSession(updatedSession);
    setShowActivityModal(false);
    setSelectedLevels([]);
    setSelectedActivities([]);
    setSelectedRecurrenceDays([]);
  };

  const handleCommonActivityToggle = (activityName, activityType) => {
    const key = `${activityType}::${activityName}`;
    setSelectedCommonActivities(prev => {
      const exists = prev.find(a => a.key === key);
      if (exists) return prev.filter(a => a.key !== key);
      return [...prev, { key, name: activityName, type: activityType }];
    });
  };

  const handleConfirmCommonActivities = () => {
    if (selectedCommonActivities.length === 0) {
      toast.error('Veuillez sélectionner au moins une activité');
      return;
    }
    const targetDays = selectedRecurrenceDays.length > 0 ? selectedRecurrenceDays : [selectedDate];
    const updatedSession = { ...session };
    targetDays.forEach(date => {
      if (!updatedSession.schedule[date]) {
        updatedSession.schedule[date] = { activities: [], attendance: {} };
      }
      if (!updatedSession.schedule[date].activities) {
        updatedSession.schedule[date].activities = [];
      }
      selectedCommonActivities.forEach(act => {
        updatedSession.schedule[date].activities.push({
          id: Date.now().toString() + Math.random(),
          hour: selectedTimeSlot,
          activity: act.name,
          activityType: act.type,
          level: 'Commun',
          activityColor: '#8b5cf6',
          levelColor: '#8b5cf6',
          isCommon: true
        });
      });
    });
    saveSession(updatedSession);
    setShowCommonActivityModal(false);
    setSelectedCommonActivities([]);
    setSelectedRecurrenceDays([]);
  };

  const displayTime = (t) => {
    const h = Math.floor(t);
    const m = Math.round((t - h) * 60);
    return `${h}h${m === 0 ? '00' : m.toString().padStart(2, '0')}`;
  };

  const handleAddTimeSlot = (timeStr) => {
    if (!timeStr) return;
    // Accepts "HH:MM" from <input type="time">
    const [hStr, mStr] = String(timeStr).includes(':') ? timeStr.split(':') : [timeStr, '0'];
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr || '0', 10);
    if (isNaN(h) || h < 0 || h > 23 || isNaN(m) || m < 0 || m > 59) {
      toast.error('Heure invalide');
      return;
    }
    const val = h + m / 60;
    const current = session.timeSlots || DEFAULT_SLOTS;
    if (current.some(s => Math.abs(s - val) < 0.001)) {
      toast.error('Ce créneau existe déjà');
      return;
    }
    saveSession({ ...session, timeSlots: [...current, val].sort((a, b) => a - b) });
    setShowTimeSlotModal(false);
    setNewTimeSlotInput('');
  };

  const handleRemoveTimeSlot = (hour) => {
    const hasActivities = Object.values(session.schedule).some(day =>
      day.activities?.some(a => Math.abs((a.hour || 0) - hour) < 0.001)
    );
    if (hasActivities) {
      toast.error('Impossible : des activités sont planifiées à cette heure');
      return;
    }
    const current = session.timeSlots || DEFAULT_SLOTS;
    saveSession({ ...session, timeSlots: current.filter(h => Math.abs(h - hour) >= 0.001) });
  };

  const handleStartEditSlot = (hour) => {
    const totalMin = Math.round(hour * 60);
    const hh = String(Math.floor(totalMin / 60)).padStart(2, '0');
    const mm = String(totalMin % 60).padStart(2, '0');
    setEditSlotInput(`${hh}:${mm}`);
    setEditingSlotHour(hour);
  };

  const handleConfirmEditSlot = (oldHour) => {
    if (!editSlotInput) { setEditingSlotHour(null); return; }
    const [hStr, mStr] = editSlotInput.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr || '0', 10);
    if (isNaN(h) || h < 0 || h > 23 || isNaN(m) || m < 0 || m > 59) {
      toast.error('Heure invalide');
      return;
    }
    const newHour = h + m / 60;
    if (Math.abs(newHour - oldHour) < 0.001) { setEditingSlotHour(null); return; }
    const current = session.timeSlots || DEFAULT_SLOTS;
    if (current.some(s => Math.abs(s - newHour) < 0.001)) {
      toast.error('Ce créneau existe déjà');
      return;
    }
    // Mettre à jour le créneau ET toutes les activités de tous les jours
    const newTimeSlots = current.map(s => Math.abs(s - oldHour) < 0.001 ? newHour : s).sort((a, b) => a - b);
    const newSchedule = {};
    Object.entries(session.schedule).forEach(([date, day]) => {
      newSchedule[date] = {
        ...day,
        activities: (day.activities || []).map(a =>
          Math.abs((a.hour || 0) - oldHour) < 0.001 ? { ...a, hour: newHour } : a
        ),
      };
    });
    saveSession({ ...session, timeSlots: newTimeSlots, schedule: newSchedule });
    setEditingSlotHour(null);
  };

  const handleActivityRemoveMultiple = (ids) => {
    const updatedSession = { ...session };
    updatedSession.schedule[selectedDate].activities =
      updatedSession.schedule[selectedDate].activities.filter(a => !ids.includes(a.id));
    saveSession(updatedSession);
  };

  const handleOpenEditActivityWithHour = (group, hour) => {
    const allIds = group.activities.flatMap(a => a.ids);
    const levelIds = group.levelsData
      .map(ld => levels.find(l => l.name === ld.name)?.id)
      .filter(Boolean);
    setEditingActivityGroup({ allIds, hour, group });
    setEditNewLevels(levelIds);
    setEditNewActivities(group.activities.map(a => ({ key: `${a.type}::${a.name}`, name: a.name, type: a.type })));
  };

  const handleConfirmEditActivity = () => {
    if (!editingActivityGroup) return;
    if (editNewLevels.length === 0) { toast.error('Sélectionnez au moins un niveau'); return; }
    if (editNewActivities.length === 0) { toast.error('Sélectionnez au moins une activité'); return; }

    const updatedSession = { ...session };
    // Remove old activities
    updatedSession.schedule[selectedDate].activities =
      updatedSession.schedule[selectedDate].activities.filter(
        a => !editingActivityGroup.allIds.includes(a.id)
      );
    // Add new activities
    editNewLevels.forEach(levelId => {
      const level = levels.find(l => l.id === levelId);
      if (!level) return;
      editNewActivities.forEach(act => {
        updatedSession.schedule[selectedDate].activities.push({
          id: Date.now().toString() + Math.random(),
          hour: editingActivityGroup.hour,
          activity: act.name,
          activityType: act.type,
          level: level.name,
          activityColor: act.type === 'théorique' ? '#10b981' : '#0ea5e9',
          levelColor: level.color
        });
      });
    });
    saveSession(updatedSession);
    setEditingActivityGroup(null);
    setEditNewLevels([]);
    setEditNewActivities([]);
  };

  const handleEditActivityToggle = (activityName, activityType) => {
    const key = `${activityType}::${activityName}`;
    setEditNewActivities(prev => {
      const exists = prev.find(a => a.key === key);
      if (exists) return prev.filter(a => a.key !== key);
      return [...prev, { key, name: activityName, type: activityType }];
    });
  };

  const handleGeneratePublicLink = async () => {
    try {
      const response = await fetch(`/api/sessions/${session.id}/generate-public-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.token) {
        setPublicLinkToken(data.token);
        setShowPublicLinkModal(true);
      } else {
        toast.error('Erreur génération du lien');
      }
    } catch (_) {
      toast.error('Erreur génération du lien');
    }
  };

  // Regroupe les activités de même contenu (mêmes activités) pour plusieurs niveaux
  const groupByContent = (acts) => {
    const byLevel = {};
    acts.forEach(a => {
      if (!byLevel[a.level]) byLevel[a.level] = { color: a.levelColor || '#8b5cf6', items: [] };
      byLevel[a.level].items.push(a);
    });
    const groups = {};
    Object.entries(byLevel).forEach(([level, data]) => {
      const sig = data.items.map(a => a.activityType + '::' + a.activity).sort().join('|');
      if (!groups[sig]) {
        groups[sig] = {
          levelsData: [],
          activities: data.items.map(a => ({ name: a.activity, type: a.activityType, activityColor: a.activityColor, ids: [] })),
        };
      }
      groups[sig].levelsData.push({ name: level, color: data.color });
      data.items.forEach(a => {
        const entry = groups[sig].activities.find(x => x.name === a.activity && x.type === a.activityType);
        if (entry) entry.ids.push(a.id);
      });
    });
    return Object.values(groups);
  };

  const handleNotesChange = (notes) => {
    const updatedSession = { ...session };
    updatedSession.schedule[selectedDate].notes = notes;
    saveSession(updatedSession);
  };

  const exportToPDF = async () => {
    // ── Charger logo Asterina ─────────────────────────────────────────
    let asterinaB64 = null;
    try {
      const resp = await fetch('/asterina.png');
      const blob = await resp.blob();
      asterinaB64 = await new Promise(resolve => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(blob);
      });
    } catch (_) {}

    // ── Logo de session (droite) — déjà en base64 s'il existe ────────
    const sessionLogo = session?.logo || null;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const dates = getDates(session.startDate, session.endDate);
    const pW = 297;
    const margin = 10;

    // ─── Helpers ───────────────────────────────────────────
    const sectionTitle = (text, x, y, w, color) => {
      doc.setFillColor(...color);
      doc.rect(x, y, w, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(text, x + 3, y + 5);
    };

    const pageFooter = (name, pageW) => {
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Club Asterina — ${name} — Exporté le ${new Date().toLocaleDateString('fr-FR')}`,
        pageW / 2, pageW === 297 ? 205 : 290, { align: 'center' }
      );
    };

    // ═══════════════════════════════════════════════════════
    // PAGE 1 — Planning grille (paysage)
    // ═══════════════════════════════════════════════════════
    const logoSz = 18;
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pW, 24, 'F');
    doc.setFillColor(14, 165, 233);
    doc.rect(0, 22, pW, 2, 'F');

    if (asterinaB64) {
      doc.addImage(asterinaB64, 'PNG', margin, 3, logoSz, logoSz, undefined, 'FAST');
    }
    if (sessionLogo) {
      doc.addImage(sessionLogo, 'PNG', pW - margin - logoSz, 3, logoSz, logoSz, undefined, 'FAST');
    } else if (asterinaB64) {
      doc.addImage(asterinaB64, 'PNG', pW - margin - logoSz, 3, logoSz, logoSz, undefined, 'FAST');
    }

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(session.name, pW / 2, 11, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    const startFmt = new Date(session.startDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const endFmt   = new Date(session.endDate).toLocaleDateString('fr-FR',   { day: '2-digit', month: 'long', year: 'numeric' });
    doc.text(`${session.location}   |   Du ${startFmt} au ${endFmt}`, pW / 2, 18, { align: 'center' });

    // ── Helpers partagés ──────────────────────────────────────────────
    const fmtH = (t) => { const h = Math.floor(t); const m = Math.round((t - h) * 60); return `${h}h${m === 0 ? '00' : m.toString().padStart(2, '0')}`; };
    const hexToRgb = (hex) => {
      const clean = (hex || '#8b5cf6').replace('#', '');
      return [parseInt(clean.slice(0,2),16), parseInt(clean.slice(2,4),16), parseInt(clean.slice(4,6),16)];
    };

    // ── Planning table — 8 jours max par page ─────────────────────────
    const DAYS_PER_PAGE = 8;
    const LINE_H        = 6.5;
    const tableStartY   = 27;
    const marginBottom  = 5;
    const availBodyH    = 210 - tableStartY - 10 - marginBottom;

    const startFmt2 = new Date(session.startDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    const endFmt2   = new Date(session.endDate).toLocaleDateString('fr-FR',   { day: '2-digit', month: 'long', year: 'numeric' });

    const dateChunks = [];
    for (let i = 0; i < dates.length; i += DAYS_PER_PAGE) {
      dateChunks.push(dates.slice(i, i + DAYS_PER_PAGE));
    }

    dateChunks.forEach((chunkDates, chunkIdx) => {
      // Page 1 est déjà créée ; ajouter une nouvelle page pour les suivantes
      if (chunkIdx > 0) {
        doc.addPage([297, 210]);
        doc.setFillColor(15, 23, 42);
        doc.rect(0, 0, pW, 24, 'F');
        doc.setFillColor(14, 165, 233);
        doc.rect(0, 22, pW, 2, 'F');
        if (asterinaB64) {
          doc.addImage(asterinaB64, 'PNG', margin, 3, logoSz, logoSz, undefined, 'FAST');
        }
        if (sessionLogo) {
          doc.addImage(sessionLogo, 'PNG', pW - margin - logoSz, 3, logoSz, logoSz, undefined, 'FAST');
        } else if (asterinaB64) {
          doc.addImage(asterinaB64, 'PNG', pW - margin - logoSz, 3, logoSz, logoSz, undefined, 'FAST');
        }
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(session.name, pW / 2, 11, { align: 'center' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(`${session.location}   |   Du ${startFmt2} au ${endFmt2}`, pW / 2, 18, { align: 'center' });
      }

      // Calcul colonnes pour ce chunk
      const timeColW  = 14;
      const dateColW  = (pW - margin * 2 - timeColW) / chunkDates.length;
      const colStyles = { 0: { cellWidth: timeColW, halign: 'center' } };
      chunkDates.forEach((_, i) => { colStyles[i + 1] = { cellWidth: dateColW }; });

      const tableHead = [['Heure', ...chunkDates.map(d =>
        new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' })
      )]];

      // colorData propre à ce chunk
      const colorData = {};
      const tableBody = timeSlots.map((hour, rowIdx) => {
        colorData[rowIdx] = {};
        const row = [fmtH(hour)];
        chunkDates.forEach((date, dIdx) => {
          const colIdx = dIdx + 1;
          const acts = (session.schedule[date]?.activities || []).filter(a => Math.abs((a.hour||0) - hour) < 0.001);
          if (acts.length === 0) { row.push(''); return; }
          // Group same-content activities across levels
          const byLevel = {};
          acts.forEach(a => {
            if (!byLevel[a.level]) byLevel[a.level] = { color: a.levelColor || '#8b5cf6', activities: [] };
            byLevel[a.level].activities.push(a.activity);
          });
          const contentGroups = {};
          Object.entries(byLevel).forEach(([lvl, d]) => {
            const sig = [...d.activities].sort().join('|');
            if (!contentGroups[sig]) contentGroups[sig] = { levelsData: [], activities: d.activities };
            contentGroups[sig].levelsData.push({ name: lvl, color: d.color });
          });
          const merged = Object.values(contentGroups).map(g => ({ levelsData: g.levelsData, activities: g.activities }));
          colorData[rowIdx][colIdx] = merged;
          row.push(merged.map(g => `${g.levelsData.map(l => l.name).join('/')}: ${g.activities.join(', ')}`).join('\n'));
        });
        return row;
      });

      const maxGroupsPerRow = timeSlots.map((_, rowIdx) => {
        let max = 1;
        chunkDates.forEach((_, dIdx) => {
          const g = colorData[rowIdx]?.[dIdx + 1];
          if (g) max = Math.max(max, g.length);
        });
        return max;
      });
      const totalNeeded = maxGroupsPerRow.reduce((s, n) => s + n * LINE_H, 0);
      const scale       = Math.min(1, availBodyH / totalNeeded);
      const rowHeights  = maxGroupsPerRow.map(n => Math.max(5, n * LINE_H * scale));

      doc.autoTable({
        startY: tableStartY,
        head: tableHead,
        body: tableBody,
        theme: 'grid',
        headStyles: {
          fillColor: [14, 165, 233],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center',
          cellPadding: 2.5
        },
        bodyStyles: {
          fontSize: 6,
          cellPadding: 1.5,
          textColor: [30, 41, 59],
          lineColor: [203, 213, 225],
          lineWidth: 0.3,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: colStyles,
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          data.cell.styles.minCellHeight = rowHeights[data.row.index] ?? 6;
          if (data.column.index === 0) {
            data.cell.styles.fillColor = [30, 41, 59];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
            return;
          }
          const groups = colorData[data.row.index]?.[data.column.index];
          if (!groups || groups.length === 0) {
            data.cell.styles.fillColor = [230, 232, 235];
          } else {
            data.cell.styles.fillColor = [255, 255, 255];
            data.cell.text = [''];
          }
        },
        didDrawCell: (data) => {
          if (data.section !== 'body' || data.column.index === 0) return;
          const { x, y, width, height } = data.cell;
          const groups = colorData[data.row.index]?.[data.column.index];

          if (!groups || groups.length === 0) {
            doc.setDrawColor(180, 185, 193);
            doc.setLineWidth(0.4);
            doc.line(x, y, x + width, y + height);
            doc.line(x + width, y, x, y + height);
            return;
          }

          const nGroups     = groups.length;
          const slotH       = height / nGroups;
          const pad         = 1.0;
          const badgeH      = 4.0;
          const textSize    = 5.5;
          const lineSpacing = textSize * 0.38;

          groups.forEach((group, gi) => {
            const slotY      = y + gi * slotH;
            const actText    = group.activities.join(', ');
            const nBadges    = group.levelsData.length;
            const perBadgeW  = Math.min(10, (width * 0.35) / nBadges);
            const totalBadgesW = nBadges * perBadgeW + (nBadges - 1) * 0.8;
            const maxTxtW    = width - pad * 2 - totalBadgesW - 2;
            const lines      = doc.splitTextToSize(actText, maxTxtW);
            const maxLines   = Math.max(1, Math.floor((slotH - pad * 2) / lineSpacing));
            const visLines   = lines.slice(0, maxLines);
            const blockH     = Math.max(badgeH, visLines.length * lineSpacing);
            const blockTop   = slotY + (slotH - blockH) / 2;
            const badgeTop   = blockTop + (blockH - badgeH) / 2;
            const badgeBase  = badgeTop + badgeH * 0.72;

            group.levelsData.forEach((ld, bi) => {
              const bx  = x + pad + bi * (perBadgeW + 0.8);
              const rgb = hexToRgb(ld.color);
              doc.setFillColor(rgb[0], rgb[1], rgb[2]);
              doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
              doc.rect(bx, badgeTop, perBadgeW, badgeH, 'F');
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(4.5);
              doc.setFont('helvetica', 'bold');
              doc.text(ld.name, bx + perBadgeW / 2, badgeBase, { align: 'center' });
            });

            const textX      = x + pad + totalBadgesW + 1.5;
            const firstLineY = blockTop + lineSpacing;
            doc.setTextColor(30, 41, 59);
            doc.setFontSize(textSize);
            doc.setFont('helvetica', 'normal');
            visLines.forEach((line, li) => {
              doc.text(line, textX, firstLineY + li * lineSpacing);
            });
          });
        },
        margin: { left: margin, right: margin, bottom: marginBottom },
        tableWidth: pW - margin * 2
      });

      pageFooter(session.name, pW);
    });

    // ═══════════════════════════════════════════════════════
    // PAGES SUIVANTES — Détail par journée (paysage)
    // Layout : [Synthèse | Présences+Moniteurs] [Mini grille planning]
    // ═══════════════════════════════════════════════════════
    dates.forEach(date => {
      doc.addPage([297, 210]);
      const pw     = 297;
      const pm     = 12;
      const usableW = pw - pm * 2;  // 273 mm

      // Dimensions colonnes gauche
      const col1W     = 58;   // Synthèse par niveau
      const colGapL   = 6;
      const col2W     = 58;   // Présences + Moniteurs
      const leftW     = col1W + colGapL + col2W;  // 122 mm
      const col1X     = pm;
      const col2X     = pm + col1W + colGapL;
      // Grille à droite
      const panelGap  = 5;
      const gridX     = pm + leftW + panelGap;    // 139 mm
      const gridW     = usableW - leftW - panelGap; // 146 mm

      const dateLabel = new Date(date).toLocaleDateString('fr-FR', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
      });
      const dateLabelCap = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);

      // En-tête de journée
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pw, 22, 'F');
      doc.setFillColor(14, 165, 233);
      doc.rect(0, 20, pw, 2, 'F');

      if (asterinaB64) {
        doc.addImage(asterinaB64, 'PNG', pm, 3, 15, 15, undefined, 'FAST');
      }
      if (sessionLogo) {
        doc.addImage(sessionLogo, 'PNG', pw - pm - 15, 3, 15, 15, undefined, 'FAST');
      } else if (asterinaB64) {
        doc.addImage(asterinaB64, 'PNG', pw - pm - 15, 3, 15, 15, undefined, 'FAST');
      }

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(dateLabelCap, pw / 2, 9, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text(session.name, pw / 2, 15, { align: 'center' });

      const startY = 26;
      const attendance    = session.schedule[date]?.attendance || { divers: {}, instructors: {} };
      const presentInstructors     = (session.instructors      || []).filter(i => attendance.instructors?.[i.id]);
      const presentNonDivers       = (session.nonDivers        || []).filter(p => attendance.nonDivers?.[p.id]);
      const presentGuidePalanquiers = (session.guidePalanquiers || []).filter(g => attendance.guidePalanquiers?.[g.id]);

      const synthData = levels
        .map(level => ({
          level,
          divers: (session.divers || []).filter(d => d.level === level.name && attendance.divers?.[d.id])
        }))
        .filter(g => g.divers.length > 0);

      let col1Y = startY;
      let col2Y = startY;
      let gridY = startY;

      // ── COL 1 : Synthèse par niveau ────────────────────────────────
      if (synthData.length > 0) {
        sectionTitle('Synthèse par niveau', col1X, col1Y, col1W, [14, 165, 233]);
        col1Y += 8;
        // Layout : 2 niveaux par ligne → [badge | nb | badge | nb]
        const sBadgeW = 18;
        const sNbW    = (col1W / 2) - sBadgeW;  // col1W=58 → 11mm
        const synthRows = [];
        for (let i = 0; i < synthData.length; i += 2) {
          const g0 = synthData[i];
          const g1 = synthData[i + 1];
          synthRows.push([
            '',
            String(g0.divers.length),
            g1 ? '' : '',
            g1 ? String(g1.divers.length) : '',
          ]);
        }
        doc.autoTable({
          startY: col1Y,
          head: [],
          body: synthRows,
          theme: 'plain',
          bodyStyles: { fontSize: 6, fontStyle: 'bold', cellPadding: 0.5, textColor: [30, 41, 59], valign: 'middle', halign: 'center', minCellHeight: 6 },
          columnStyles: {
            0: { cellWidth: sBadgeW },
            1: { cellWidth: sNbW,   halign: 'center' },
            2: { cellWidth: sBadgeW },
            3: { cellWidth: sNbW,   halign: 'center' },
          },
          didDrawCell: (data) => {
            if (data.section !== 'body') return;
            if (data.column.index !== 0 && data.column.index !== 2) return;
            const gIdx = data.row.index * 2 + (data.column.index === 2 ? 1 : 0);
            const g = synthData[gIdx];
            if (!g) return;
            const { x, y, width, height } = data.cell;
            const pad    = 1;
            const badgeH = height - pad * 2;
            const badgeW = width - pad * 2;
            const bx     = x + pad;
            const by     = y + pad;
            const rgb    = hexToRgb(g.level.color || '#8b5cf6');
            doc.setFillColor(rgb[0], rgb[1], rgb[2]);
            doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
            doc.rect(bx, by, badgeW, badgeH, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(4);
            doc.setFont('helvetica', 'bold');
            doc.text(g.level.name, bx + badgeW / 2, by + badgeH * 0.65, { align: 'center' });
          },
          margin: { left: col1X },
          tableWidth: col1W
        });
        col1Y = doc.lastAutoTable.finalY + 5;
      }

      // ── COL 1 : Moniteurs présents (sous synthèse) ─────────────────
      sectionTitle('Moniteurs présents', col1X, col1Y, col1W, [99, 102, 241]);
      col1Y += 8;
      (() => {
        if (presentInstructors.length === 0) return;
        const byLevel = {};
        const levelOrder = [];
        presentInstructors.forEach(i => {
          const lvl = i.level || '-';
          if (!byLevel[lvl]) { byLevel[lvl] = []; levelOrder.push(lvl); }
          byLevel[lvl].push(i.name);
        });
        const monRows  = levelOrder.map(lvl => ['', byLevel[lvl].join(', ')]);
        const monBadgeW = 14;
        doc.autoTable({
          startY: col1Y,
          head: [],
          body: monRows,
          theme: 'plain',
          bodyStyles: { fontSize: 6, cellPadding: 0.5, textColor: [30, 41, 59], valign: 'middle', minCellHeight: 6 },
          columnStyles: {
            0: { cellWidth: monBadgeW },
            1: { cellWidth: col1W - monBadgeW },
          },
          didDrawCell: (data) => {
            if (data.section !== 'body' || data.column.index !== 0) return;
            const lvl  = levelOrder[data.row.index];
            if (!lvl) return;
            const levelObj = levels.find(l => l.name === lvl);
            const color    = levelObj?.color || '#6366f1';
            const { x, y, width, height } = data.cell;
            const pad    = 1;
            const bh     = height - pad * 2;
            const bw     = width  - pad * 2;
            const rgb    = hexToRgb(color);
            doc.setFillColor(rgb[0], rgb[1], rgb[2]);
            doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
            doc.rect(x + pad, y + pad, bw, bh, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(4);
            doc.setFont('helvetica', 'bold');
            doc.text(lvl, x + pad + bw / 2, y + pad + bh * 0.65, { align: 'center' });
          },
          margin: { left: col1X },
          tableWidth: col1W
        });
        col1Y = doc.lastAutoTable.finalY + 5;
      })();

      // ── COL 2 : Présences plongeurs ────────────────────────────────
      sectionTitle('Présences du jour', col2X, col2Y, col2W, [16, 185, 129]);
      col2Y += 8;
      const levelColorMap = {};
      levels.forEach(l => { levelColorMap[l.name] = l.color; });
      (session.divers || []).forEach(d => { if (d.levelColor) levelColorMap[d.level] = d.levelColor; });

      const allDiversSorted = [...(session.divers || [])]
        .map(d => ({ ...d, present: !!attendance.divers?.[d.id], levelColor: levelColorMap[d.level] || '#8b5cf6' }))
        .sort((a, b) => {
          const li = levels.findIndex(l => l.name === a.level);
          const lj = levels.findIndex(l => l.name === b.level);
          if (li !== lj) return li - lj;
          return (b.present ? 1 : 0) - (a.present ? 1 : 0);
        });

      const badgeColW = 13;
      doc.autoTable({
        startY: col2Y,
        head: [],
        showHead: 'never',
        body: allDiversSorted.length > 0
          ? allDiversSorted.map(d => ['', d.name, ''])
          : [['', '-', '']],
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59], textColor: [148, 163, 184], fontSize: 7, fontStyle: 'bold', cellPadding: 2 },
        bodyStyles: { fontSize: 7, cellPadding: 1.5, textColor: [30, 41, 59] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 7,                       halign: 'center' },
          1: { cellWidth: col2W - 7 - badgeColW },
          2: { cellWidth: badgeColW,               halign: 'center' }
        },
        didDrawCell: (data) => {
          if (data.section !== 'body') return;
          const { x, y, width, height } = data.cell;
          const rowData = allDiversSorted[data.row.index];
          if (!rowData) return;

          // Col 0 : case cochée / vide
          if (data.column.index === 0) {
            const boxSize = 3;
            const bx = x + (width  - boxSize) / 2;
            const by = y + (height - boxSize) / 2;
            if (rowData.present) {
              doc.setFillColor(16, 185, 129);
              doc.setDrawColor(16, 185, 129);
              doc.rect(bx, by, boxSize, boxSize, 'F');
              doc.setDrawColor(255, 255, 255);
              doc.setLineWidth(0.5);
              doc.line(bx + 0.5,            by + boxSize * 0.55, bx + boxSize * 0.42, by + boxSize - 0.5);
              doc.line(bx + boxSize * 0.42,  by + boxSize - 0.5,  bx + boxSize - 0.4,  by + 0.5);
            } else {
              doc.setDrawColor(160, 170, 185);
              doc.setLineWidth(0.4);
              doc.rect(bx, by, boxSize, boxSize, 'S');
            }
          }

          // Col 2 : badge niveau coloré
          if (data.column.index === 2) {
            const badgeH  = 4;
            const badgeW  = width - 2;
            const bx      = x + 1;
            const by      = y + (height - badgeH) / 2;
            const rgb     = hexToRgb(rowData.levelColor);
            doc.setFillColor(rgb[0], rgb[1], rgb[2]);
            doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
            doc.rect(bx, by, badgeW, badgeH, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(4.5);
            doc.setFont('helvetica', 'bold');
            doc.text(rowData.level || '', bx + badgeW / 2, by + badgeH * 0.72, { align: 'center' });
          }
        },
        margin: { left: col2X },
        tableWidth: col2W
      });
      col2Y = doc.lastAutoTable.finalY + 5;

      // ── COL 1 : Guides de Palanquier ───────────────────────────────
      if (presentGuidePalanquiers.length > 0) {
        sectionTitle('Guides GP', col1X, col1Y, col1W, [139, 92, 246]);
        col1Y += 8;
        const gpRows = presentGuidePalanquiers.map(g => ['', `${g.name}${g.role ? ' · ' + g.role : ''}`]);
        doc.autoTable({
          startY: col1Y,
          head: [],
          body: gpRows,
          theme: 'plain',
          bodyStyles: { fontSize: 6, cellPadding: 0.5, textColor: [30, 41, 59], valign: 'middle', minCellHeight: 6 },
          columnStyles: {
            0: { cellWidth: 5 },
            1: { cellWidth: col1W - 5 },
          },
          didDrawCell: (data) => {
            if (data.section !== 'body' || data.column.index !== 0) return;
            const { x, y, width, height } = data.cell;
            const r = (height - 2) / 2;
            doc.setFillColor(139, 92, 246);
            doc.setDrawColor(139, 92, 246);
            doc.circle(x + width / 2, y + height / 2, r, 'F');
          },
          margin: { left: col1X },
          tableWidth: col1W
        });
        col1Y = doc.lastAutoTable.finalY + 5;
      }

      // ── COL 1 : Non plongeurs ─────────────────────────────────────
      if (presentNonDivers.length > 0) {
        sectionTitle('Non Plongeurs', col1X, col1Y, col1W, [245, 158, 11]);
        col1Y += 8;
        const npRows = presentNonDivers.map(p => ['', `${p.name}${p.role ? ' · ' + p.role : ''}`]);
        doc.autoTable({
          startY: col1Y,
          head: [],
          body: npRows,
          theme: 'plain',
          bodyStyles: { fontSize: 6, cellPadding: 0.5, textColor: [30, 41, 59], valign: 'middle', minCellHeight: 6 },
          columnStyles: {
            0: { cellWidth: 5 },
            1: { cellWidth: col1W - 5 },
          },
          didDrawCell: (data) => {
            if (data.section !== 'body' || data.column.index !== 0) return;
            const { x, y, width, height } = data.cell;
            const r = (height - 2) / 2;
            doc.setFillColor(245, 158, 11);
            doc.setDrawColor(245, 158, 11);
            doc.circle(x + width / 2, y + height / 2, r, 'F');
          },
          margin: { left: col1X },
          tableWidth: col1W
        });
        col1Y = doc.lastAutoTable.finalY + 5;
      }

      // ── DROITE : Mini grille planning (comme page 1, une seule journée) ──
      const dayActs = session.schedule[date]?.activities || [];

      // Carte niveau → couleur
      const dayLevelColors = {};
      levels.forEach(l => { dayLevelColors[l.name] = l.color; });
      dayActs.forEach(a => { if (a.level && a.levelColor) dayLevelColors[a.level] = a.levelColor; });

      // Niveaux présents ce jour, dans l'ordre de paramétrage
      const knownLvlOrder = (levels || []).map(l => l.name);
      const dayLevelSet   = new Set(dayActs.map(a => a.level));
      const dayLevels     = [
        ...knownLvlOrder.filter(n => dayLevelSet.has(n)),
        ...[...dayLevelSet].filter(n => !knownLvlOrder.includes(n)),
      ];

      if (dayLevels.length > 0) {
        sectionTitle('Planning du jour', gridX, gridY, gridW, [15, 23, 42]);
        gridY += 8;

        const gridByHour = {};
        dayActs.forEach(a => {
          if (!gridByHour[a.hour]) gridByHour[a.hour] = {};
          if (!gridByHour[a.hour][a.level]) gridByHour[a.hour][a.level] = [];
          gridByHour[a.hour][a.level].push(a.activity);
        });
        const gridHours = Object.keys(gridByHour).sort((a, b) => Number(a) - Number(b));

        const timeColWDay = 14;
        const lvlColWDay  = (gridW - timeColWDay) / dayLevels.length;

        const dayGridColStyles = { 0: { cellWidth: timeColWDay, halign: 'center', fontStyle: 'bold' } };
        dayLevels.forEach((_, i) => { dayGridColStyles[i + 1] = { cellWidth: lvlColWDay }; });

        const gridHead = [['Heure', ...dayLevels.map(() => '')]];
        const gridBody = gridHours.map(hour => {
          const h = Math.floor(Number(hour));
          const m = Math.round((Number(hour) - h) * 60);
          return [
            `${h}h${m === 0 ? '00' : m.toString().padStart(2, '0')}`,
            ...dayLevels.map(lvl => (gridByHour[hour][lvl] || []).join(', ') || ''),
          ];
        });

        doc.autoTable({
          startY: gridY,
          head: gridHead,
          body: gridBody,
          theme: 'grid',
          headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold', cellPadding: 2, halign: 'center', minCellHeight: 7 },
          bodyStyles: { fontSize: 7, cellPadding: 2, textColor: [30, 41, 59] },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: dayGridColStyles,
          margin: { left: gridX },
          tableWidth: gridW,
          didDrawCell: (data) => {
            // Colorier les en-têtes de niveaux
            if (data.section === 'head' && data.column.index > 0) {
              const lvl   = dayLevels[data.column.index - 1];
              const color = dayLevelColors[lvl];
              if (color) {
                const rgb = hexToRgb(color);
                const { x, y: cy, width, height } = data.cell;
                doc.setFillColor(...rgb);
                doc.rect(x, cy, width, height, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(7);
                doc.setFont('helvetica', 'bold');
                doc.text(lvl, x + width / 2, cy + height * 0.65, { align: 'center' });
              }
            }
          },
        });
        gridY = doc.lastAutoTable.finalY + 5;
      }

      // ── Notes (pleine largeur, si espace) ──────────────────────────
      const notes  = session.schedule[date]?.notes;
      const notesY = Math.max(col1Y, col2Y, gridY) + 3;
      if (notes && notesY < 185) {
        sectionTitle('Notes', pm, notesY, usableW, [245, 158, 11]);
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(doc.splitTextToSize(notes, usableW), pm, notesY + 12);
      }

      pageFooter(session.name, pw);
    });

    doc.save(`planning-${session.name}.pdf`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement du planning...</p>
      </div>
    );
  }

  if (!session) {
    return <div className="error-message">Session non trouvée</div>;
  }

  const dates = getDates(session.startDate, session.endDate);
  const daySchedule = session.schedule[selectedDate] || { activities: [], attendance: { divers: {}, instructors: {} }, notes: '' };

  return (
    <div className="session-planning">
      <div className="planning-header">
        <button onClick={() => navigate('/sessions')} className="btn-back"><Icon name="back" /> Retour</button>
        <div className="header-info">
          <img
            src={session.logo || '/asterina.png'}
            alt="Logo"
            className="session-header-logo"
          />
          <div>
            <h1>{session.name}</h1>
            <p>{session.location}</p>
          </div>
        </div>
        <div className="header-buttons">
          <button onClick={handleGeneratePublicLink} className="btn-export" style={{ background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)' }}>
            <Icon name="link" /> Lien public
          </button>
          <button onClick={exportToPDF} className="btn-export"><Icon name="pdf" /> Exporter PDF</button>
        </div>
      </div>

      <div className="date-selector">
        {dates.map((date) => (
          <button
            key={date}
            onClick={() => setSelectedDate(date)}
            className={`date-btn ${selectedDate === date ? 'active' : ''}`}
          >
            <div className="date-day">{new Date(date).toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
            <div className="date-number">{new Date(date).getDate()}</div>
            <div className="date-month">{new Date(date).toLocaleDateString('fr-FR', { month: 'short' })}</div>
          </button>
        ))}
      </div>

      {selectedDate && (
        <div className="planning-content">
          <div className="planning-actions">
            <button 
              onClick={() => setShowAttendanceModal(true)}
              className="btn-attendance"
            >
              <Icon name="check" /> Appel
            </button>
          </div>

          <div className="schedule-table">
            <div className="table-header">
              <div className="time-column">Heure</div>
              <div className="activity-column">Activités</div>
            </div>
            
            {timeSlots.map((hour) => {
              const hourActivities = daySchedule.activities?.filter(a => a.hour === hour) || [];
              
              return (
                <div key={hour} className="table-row">
                  <div className="time-cell">
                    {editingSlotHour === hour ? (
                      <div className="timeslot-edit-inline">
                        <input
                          type="time"
                          value={editSlotInput}
                          onChange={e => setEditSlotInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleConfirmEditSlot(hour); if (e.key === 'Escape') setEditingSlotHour(null); }}
                          autoFocus
                          className="timeslot-edit-input"
                        />
                        <button className="btn-timeslot-confirm" onClick={() => handleConfirmEditSlot(hour)} title="Confirmer">✓</button>
                        <button className="btn-timeslot-cancel" onClick={() => setEditingSlotHour(null)} title="Annuler">✕</button>
                      </div>
                    ) : (
                      <>
                        <span>{displayTime(hour)}</span>
                        <button
                          className="btn-edit-timeslot"
                          onClick={() => handleStartEditSlot(hour)}
                          title="Modifier l'heure"
                        ><Icon name="edit" /></button>
                        {hourActivities.length === 0 && (
                          <button
                            className="btn-remove-timeslot"
                            onClick={() => handleRemoveTimeSlot(hour)}
                            title="Supprimer ce créneau"
                          >×</button>
                        )}
                      </>
                    )}
                  </div>
                  <div className="activity-cell">
                    {(() => {
                      const groups = groupByContent(hourActivities);
                      return groups.map((group, gIdx) => (
                        <div key={gIdx} className="activity-group-item" style={{ borderLeftColor: group.levelsData[0].color }}>
                          <span className="activity-group-levels">
                            {group.levelsData.map((ld, li) => (
                              <span key={li} className="activity-group-level" style={{ backgroundColor: ld.color }}>{ld.name}</span>
                            ))}
                          </span>
                          <div className="activity-group-list">
                            {group.activities.map((act, aIdx) => (
                              <span key={aIdx} className={`activity-chip-item ${act.type === 'théorique' ? 'theoretical' : 'practical'}`}>
                                {act.name}
                                <button
                                  onClick={() => handleActivityRemoveMultiple(act.ids)}
                                  className="btn-remove-chip"
                                >✕</button>
                              </span>
                            ))}
                          </div>
                          <button
                            className="btn-edit-activity-group"
                            onClick={() => handleOpenEditActivityWithHour(group, hour)}
                            title="Modifier cette activité"
                          ><Icon name="edit" /></button>
                        </div>
                      ));
                    })()}
                    <div className="activity-add-buttons">
                      <button 
                        onClick={() => {
                          setSelectedTimeSlot(hour);
                          setSelectedActivities([]);
                          setSelectedRecurrenceDays([selectedDate]);
                          setShowActivityModal(true);
                        }}
                        className="btn-add-activity"
                      >
                        + Ajouter une activité
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedTimeSlot(hour);
                          setSelectedCommonActivities([]);
                          setSelectedRecurrenceDays([selectedDate]);
                          setShowCommonActivityModal(true);
                        }}
                        className="btn-add-activity btn-add-common"
                      >
                        + Activité commune
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="table-row add-timeslot-row">
              <div className="time-cell add-timeslot-cell">
                <button className="btn-add-timeslot" onClick={() => setShowTimeSlotModal(true)}>
                  <Icon name="plus" /> Heure
                </button>
              </div>
              <div className="activity-cell"></div>
            </div>
          </div>

          <div className="synthesis-section">
            <h3><Icon name="stats" /> Synthèse par niveau</h3>
            <div className="synthesis-grid">
              {levels.map((level) => {
                const diversAtLevel = session.divers?.filter(d => d.level === level.name) || [];
                if (diversAtLevel.length === 0) return null;
                const rawLvl = daySchedule.attendance?.levelInstructors?.[level.id];
                const assignedIds = Array.isArray(rawLvl) ? rawLvl : (rawLvl ? [rawLvl] : []);
                const allEncadrants = [...(session.instructors || []), ...(session.guidePalanquiers || [])];
                const assignedInstructors = assignedIds
                  .map(id => allEncadrants.find(i => i.id === id))
                  .filter(Boolean);
                return (
                  <div key={level.id} className="synthesis-card" style={{ borderLeftColor: level.color }}>
                    <div className="synthesis-level" style={{ backgroundColor: level.color }}>
                      {level.name}
                    </div>
                    <div className="synthesis-info">
                      <div className="synthesis-count">
                        <Icon name="users" />
                        <strong>{diversAtLevel.length}</strong> plongeur{diversAtLevel.length > 1 ? 's' : ''}
                      </div>
                      {assignedInstructors.length > 0 ? (
                        <div className="synthesis-instructors">
                          <Icon name="user" />
                          {assignedInstructors.map(i => i.name).join(', ')}
                        </div>
                      ) : (
                        <div className="synthesis-instructors synthesis-no-instructor">
                          <Icon name="user" /> Non assigné
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {levels.every(level => !(session.divers?.some(d => d.level === level.name))) && (
                <p className="empty-activities">Aucun plongeur inscrit dans cette session</p>
              )}
              {session.nonDivers && session.nonDivers.length > 0 && (
                <div className="synthesis-card" style={{ borderLeftColor: '#f59e0b' }}>
                  <div className="synthesis-level" style={{ backgroundColor: '#f59e0b', color: '#fff' }}>
                    Non Plongeurs
                  </div>
                  <div className="synthesis-info">
                    <div className="synthesis-count">
                      <Icon name="users" />
                      <strong>{session.nonDivers.length}</strong> personne{session.nonDivers.length > 1 ? 's' : ''}
                    </div>
                    <div className="synthesis-instructors">
                      {session.nonDivers.map(p => p.name + (p.role ? ` (${p.role})` : '')).join(', ')}
                    </div>
                  </div>
                </div>
              )}
              {session.guidePalanquiers && session.guidePalanquiers.length > 0 && (
                <div className="synthesis-card" style={{ borderLeftColor: '#8b5cf6' }}>
                  <div className="synthesis-level" style={{ backgroundColor: '#8b5cf6', color: '#fff' }}>
                    Guides GP
                  </div>
                  <div className="synthesis-info">
                    <div className="synthesis-count">
                      <Icon name="users" />
                      <strong>{session.guidePalanquiers.length}</strong> guide{session.guidePalanquiers.length > 1 ? 's' : ''}
                    </div>
                    <div className="synthesis-instructors">
                      {session.guidePalanquiers.map(g => g.name + (g.role ? ` (${g.role})` : '')).join(', ')}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="attendance-display">
            <h3><Icon name="users" /> Présences du jour</h3>
            <div className="attendance-groups">
              <div className="attendance-group">
                <h4><Icon name="user" /> Plongeurs ({session.divers?.filter(d => daySchedule.attendance?.divers?.[d.id]).length || 0}/{session.divers?.length || 0})</h4>
                <div className="attendance-tags">
                  {session.divers && session.divers.length > 0 ? (
                    session.divers.map((diver) => (
                      <div key={diver.id} className={`attendance-tag ${daySchedule.attendance?.divers?.[diver.id] ? '' : 'absent'}`}>
                        <span>{diver.name}</span>
                        <span className="level-badge">{diver.level}</span>
                      </div>
                    ))
                  ) : (
                    <p className="empty-activities">Aucun plongeur inscrit</p>
                  )}
                </div>
              </div>
              
              <div className="attendance-group">
                <h4><Icon name="user" /> Moniteurs ({session.instructors?.filter(i => daySchedule.attendance?.instructors?.[i.id]).length || 0}/{session.instructors?.length || 0})</h4>
                <div className="attendance-tags">
                  {session.instructors && session.instructors.length > 0 ? (
                    session.instructors.map((instructor) => (
                      <div key={instructor.id} className={`attendance-tag ${daySchedule.attendance?.instructors?.[instructor.id] ? '' : 'absent'}`}>
                        <span>{instructor.name}</span>
                        <span className="level-badge">{instructor.level}</span>
                      </div>
                    ))
                  ) : (
                    <p className="empty-activities">Aucun moniteur inscrit</p>
                  )}
                </div>
              </div>

              {session.nonDivers && session.nonDivers.length > 0 && (
                <div className="attendance-group">
                  <h4><Icon name="user" /> Non Plongeurs ({session.nonDivers.filter(p => daySchedule.attendance?.nonDivers?.[p.id]).length}/{session.nonDivers.length})</h4>
                  <div className="attendance-tags">
                    {session.nonDivers.map((p) => (
                      <div key={p.id} className={`attendance-tag attendance-tag-nonplongeur ${daySchedule.attendance?.nonDivers?.[p.id] ? '' : 'absent'}`}>
                        <span>{p.name}</span>
                        {p.role && <span className="level-badge">{p.role}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {session.guidePalanquiers && session.guidePalanquiers.length > 0 && (
                <div className="attendance-group">
                  <h4><Icon name="user" /> Guides de Palanquier ({session.guidePalanquiers.filter(g => daySchedule.attendance?.guidePalanquiers?.[g.id]).length}/{session.guidePalanquiers.length})</h4>
                  <div className="attendance-tags">
                    {session.guidePalanquiers.map((g) => (
                      <div key={g.id} className={`attendance-tag attendance-tag-guide ${daySchedule.attendance?.guidePalanquiers?.[g.id] ? '' : 'absent'}`}>
                        <span>{g.name}</span>
                        {g.role && <span className="level-badge">{g.role}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="notes-section">
            <h3>Notes du jour</h3>
            <textarea
              value={daySchedule.notes || ''}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Ajoutez des notes pour cette journée..."
              rows="4"
            />
          </div>

          <div className="fiches-section">
            <div className="fiches-section-header">
              <h3><Icon name="clipboard" /> Fiches de palanquée ({dayFiches.length})</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setShowBlankFicheModal(true)} className="btn-add-fiche" style={{ background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)' }}>
                  <Icon name="pdf" /> Fiche vierge
                </button>
                <button onClick={handleOpenNewFiche} className="btn-add-fiche">
                  <Icon name="plus" /> Ajouter une fiche
                </button>
              </div>
            </div>

            {dayFiches.length === 0 && (
              <p className="empty-activities" style={{ textAlign: 'center', padding: '1.25rem' }}>
                Aucune fiche pour ce jour. Cliquez sur « Ajouter une fiche ».
              </p>
            )}

            {dayFiches.map((fiche, idx) => (
              <div key={fiche.id} className="fiche-row">
                <div className="fiche-row-info">
                  <span className="fiche-row-num">Fiche {idx + 1}</span>
                  {fiche.bateau && <span className="fiche-row-bateau"><Icon name="diving" /> {fiche.bateau}</span>}
                  {fiche.site   && <span className="fiche-row-site">{fiche.site}</span>}
                  {fiche.palanquees?.length > 0 && (
                    <span className="fiche-row-meta">{fiche.palanquees.length} palanquée{fiche.palanquees.length > 1 ? 's' : ''}</span>
                  )}
                  {fiche.savedAt && (
                    <span className="fiche-row-saved">
                      <Icon name="check" /> {new Date(fiche.savedAt).toLocaleString('fr-FR')}
                    </span>
                  )}
                </div>
                <div className="fiche-row-actions">
                  <button onClick={() => handleOpenEditFiche(fiche)} className="btn-fiche-action btn-fiche-edit">
                    <Icon name="edit" /> Modifier
                  </button>
                  <button onClick={() => handleOpenCopyFiche(fiche)} className="btn-fiche-action btn-fiche-copy">
                    <Icon name="copy" /> Copier
                  </button>
                  <button onClick={() => handleDownloadFichePDF(fiche)} className="btn-fiche-action btn-fiche-dl">
                    <Icon name="pdf" /> PDF
                  </button>
                  <button onClick={() => handleDeleteFiche(fiche.id)} className="btn-fiche-action btn-fiche-del">
                    <Icon name="trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal d'appel */}
      {showAttendanceModal && (
        <div className="modal-overlay" onClick={() => setShowAttendanceModal(false)}>
          <div className="modal-content attendance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Appel - {formatDate(selectedDate)}</h2>
              <button onClick={() => setShowAttendanceModal(false)} className="close-btn"><Icon name="close" /></button>
            </div>

            <div className="attendance-content">
              <div className="attendance-section">
                <h3><Icon name="user" /> Plongeurs</h3>
                {session.divers && session.divers.length > 0 ? (
                  <div className="attendance-list">
                    {session.divers.map((diver) => (
                      <label key={diver.id} className="attendance-item">
                        <input
                          type="checkbox"
                          checked={daySchedule.attendance?.divers?.[diver.id] || false}
                          onChange={(e) => handleAttendanceChange('divers', diver.id, e.target.checked)}
                        />
                        <span className="person-name">{diver.name}</span>
                        <span className="person-level">{diver.level}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="empty-list">Aucun plongeur inscrit</p>
                )}
              </div>

              <div className="attendance-section">
                <h3><Icon name="user" /> Moniteurs</h3>
                {session.instructors && session.instructors.length > 0 ? (
                  <div className="attendance-list">
                    {session.instructors.map((instructor) => (
                      <label key={instructor.id} className="attendance-item">
                        <input
                          type="checkbox"
                          checked={daySchedule.attendance?.instructors?.[instructor.id] || false}
                          onChange={(e) => handleAttendanceChange('instructors', instructor.id, e.target.checked)}
                        />
                        <span className="person-name">{instructor.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="empty-list">Aucun moniteur inscrit</p>
                )}
              </div>

              {session.nonDivers && session.nonDivers.length > 0 && (
                <div className="attendance-section">
                  <h3><Icon name="user" /> Non Plongeurs</h3>
                  <div className="attendance-list">
                    {session.nonDivers.map((p) => (
                      <label key={p.id} className="attendance-item">
                        <input
                          type="checkbox"
                          checked={daySchedule.attendance?.nonDivers?.[p.id] || false}
                          onChange={(e) => handleAttendanceChange('nonDivers', p.id, e.target.checked)}
                        />
                        <span className="person-name">{p.name}</span>
                        {p.role && <span className="person-level">{p.role}</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {session.guidePalanquiers && session.guidePalanquiers.length > 0 && (
                <div className="attendance-section">
                  <h3><Icon name="user" /> Guides de Palanquier</h3>
                  <div className="attendance-list">
                    {session.guidePalanquiers.map((g) => (
                      <label key={g.id} className="attendance-item">
                        <input
                          type="checkbox"
                          checked={daySchedule.attendance?.guidePalanquiers?.[g.id] || false}
                          onChange={(e) => handleAttendanceChange('guidePalanquiers', g.id, e.target.checked)}
                        />
                        <span className="person-name">{g.name}</span>
                        {g.role && <span className="person-level">{g.role}</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {levels.some(l => session.divers?.some(d => d.level === l.name)) && (
                <div className="attendance-section">
                  <h3><Icon name="stats" /> Moniteur par niveau</h3>
                  <div className="level-instructor-list">
                    {levels.map((level) => {
                      const diversAtLevel = session.divers?.filter(d => d.level === level.name) || [];
                      if (diversAtLevel.length === 0) return null;
                      const raw = daySchedule.attendance?.levelInstructors?.[level.id];
                      const assignedArr = Array.isArray(raw) ? raw : (raw ? [raw] : []);
                      return (
                        <div key={level.id} className="level-instructor-row" style={{ alignItems: 'flex-start' }}>
                          <span className="level-instructor-badge" style={{ backgroundColor: level.color, marginTop: '0.25rem' }}>
                            {level.name}
                          </span>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', flex: 1 }}>
                            {[...(session.instructors || []), ...(session.guidePalanquiers || [])].map(i => {
                              const checked = assignedArr.includes(i.id);
                              const isGuide = !(session.instructors || []).find(ins => ins.id === i.id);
                              return (
                                <label key={i.id} className="attendance-item" style={{ margin: 0, padding: '0.3rem 0.65rem', borderRadius: '6px', background: checked ? 'rgba(14,165,233,0.18)' : 'rgba(255,255,255,0.04)', border: `1px solid ${checked ? '#0ea5e9' : 'rgba(255,255,255,0.08)'}`, cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => handleLevelInstructorChange(level.id, i.id)}
                                  />
                                  <span className="person-name" style={{ fontSize: '0.82rem' }}>{i.name}</span>
                                  {isGuide && <span style={{ fontSize: '0.7rem', color: '#f59e0b', marginLeft: '0.2rem' }}>GP</span>}
                                  {!isGuide && daySchedule.attendance?.instructors?.[i.id] && <span style={{ color: '#10b981', fontSize: '0.75rem', marginLeft: '0.25rem' }}>✓</span>}
                                  {isGuide && daySchedule.attendance?.guidePalanquiers?.[i.id] && <span style={{ color: '#10b981', fontSize: '0.75rem', marginLeft: '0.25rem' }}>✓</span>}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout d'activité */}
      {showActivityModal && (
        <div className="modal-overlay" onClick={() => { setShowActivityModal(false); setSelectedActivities([]); setSelectedLevels([]); setSelectedRecurrenceDays([]); }}>
          <div className="modal-content activity-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajouter des activités — {displayTime(selectedTimeSlot)}</h2>
              <button onClick={() => { setShowActivityModal(false); setSelectedActivities([]); setSelectedLevels([]); setSelectedRecurrenceDays([]); }} className="close-btn"><Icon name="close" /></button>
            </div>

            <div className="activity-selection">
              <div className="level-selector-group">
                <h3>1. Sélectionnez les niveaux</h3>
                <div className="level-checkboxes">
                  {levels.map((level) => (
                    <label
                      key={level.id}
                      className={`level-checkbox-item ${selectedLevels.includes(level.id) ? 'checked' : ''}`}
                      style={{ borderColor: selectedLevels.includes(level.id) ? level.color : 'transparent', background: selectedLevels.includes(level.id) ? level.color + '22' : '' }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedLevels.includes(level.id)}
                        onChange={(e) => {
                          setSelectedLevels(prev =>
                            e.target.checked ? [...prev, level.id] : prev.filter(id => id !== level.id)
                          );
                        }}
                      />
                      <span className="level-dot" style={{ background: level.color }}></span>
                      {level.name}
                    </label>
                  ))}
                  {levels.length > 1 && (
                    <button
                      className="btn-select-all-levels"
                      onClick={() => setSelectedLevels(selectedLevels.length === levels.length ? [] : levels.map(l => l.id))}
                    >
                      {selectedLevels.length === levels.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </button>
                  )}
                </div>
              </div>

              <h3>2. Choisissez les activités</h3>
              
              <div className="activity-columns">
                <div className="activity-column">
                  <h4><Icon name="clipboard" /> Activités Théoriques</h4>
                  <div className="activity-list">
                    {activities.theoretical?.map((activityName, idx) => {
                      const key = `théorique::${activityName}`;
                      const isSelected = selectedActivities.some(a => a.key === key);
                      return (
                        <button
                          key={idx}
                          onClick={() => handleActivityToggle(activityName, 'théorique')}
                          className={`activity-btn theoretical ${isSelected ? 'selected' : ''}`}
                          disabled={selectedLevels.length === 0}
                        >
                          {isSelected && <Icon name="check" />} {activityName}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div className="activity-column">
                  <h4><Icon name="diving" /> Activités Pratiques</h4>
                  <div className="activity-list">
                    {activities.practical?.map((activityName, idx) => {
                      const key = `pratique::${activityName}`;
                      const isSelected = selectedActivities.some(a => a.key === key);
                      return (
                        <button
                          key={idx}
                          onClick={() => handleActivityToggle(activityName, 'pratique')}
                          className={`activity-btn practical ${isSelected ? 'selected' : ''}`}
                          disabled={selectedLevels.length === 0}
                        >
                          {isSelected && <Icon name="check" />} {activityName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="recurrence-selector">
                <h3>3. Récurrence — Appliquer aussi les jours :</h3>
                <div className="recurrence-days">
                  {getDates(session.startDate, session.endDate).map(date => (
                    <label
                      key={date}
                      className={`recurrence-day-item ${selectedRecurrenceDays.includes(date) ? 'checked' : ''} ${date === selectedDate ? 'current-day' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRecurrenceDays.includes(date)}
                        onChange={e => {
                          setSelectedRecurrenceDays(prev =>
                            e.target.checked ? [...prev, date] : prev.filter(d => d !== date)
                          );
                        }}
                      />
                      <span className="recurrence-day-label">
                        <span className="recurrence-day-short">{new Date(date).toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                        <span className="recurrence-day-num">{new Date(date).getDate()}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="activity-confirm-bar">
                {selectedActivities.length > 0 && (
                  <span className="selected-count">
                    {selectedActivities.length} activité{selectedActivities.length > 1 ? 's' : ''} × {selectedLevels.length} niveau{selectedLevels.length > 1 ? 'x' : ''}
                    {selectedRecurrenceDays.length > 1 && <> × {selectedRecurrenceDays.length} jours</>}
                  </span>
                )}
                <button
                  onClick={handleConfirmActivities}
                  className="btn-confirm-activities"
                  disabled={selectedLevels.length === 0 || selectedActivities.length === 0}
                >
                  <Icon name="check" /> Confirmer{selectedRecurrenceDays.length > 1 ? ` (${selectedRecurrenceDays.length} jours)` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Activité Commune */}
      {showCommonActivityModal && (
        <div className="modal-overlay" onClick={() => { setShowCommonActivityModal(false); setSelectedCommonActivities([]); setSelectedRecurrenceDays([]); }}>
          <div className="modal-content activity-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Activité commune — {displayTime(selectedTimeSlot)}</h2>
              <button onClick={() => { setShowCommonActivityModal(false); setSelectedCommonActivities([]); setSelectedRecurrenceDays([]); }} className="close-btn"><Icon name="close" /></button>
            </div>
            <div className="activity-selection">
              <p className="common-activity-info"><Icon name="users" /> Ces activités seront affichées pour <strong>tous les niveaux</strong>.</p>
              <div className="activity-columns activity-columns-common">
                <div className="activity-column">
                  <h4><Icon name="clipboard" /> Théoriques</h4>
                  <div className="activity-list">
                    {activities.theoretical?.map((activityName, idx) => {
                      const key = `théorique::${activityName}`;
                      const isSelected = selectedCommonActivities.some(a => a.key === key);
                      return (
                        <button key={idx} onClick={() => handleCommonActivityToggle(activityName, 'théorique')}
                          className={`activity-btn theoretical ${isSelected ? 'selected' : ''}`}>
                          {isSelected && <Icon name="check" />} {activityName}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="activity-column">
                  <h4><Icon name="diving" /> Pratiques</h4>
                  <div className="activity-list">
                    {activities.practical?.map((activityName, idx) => {
                      const key = `pratique::${activityName}`;
                      const isSelected = selectedCommonActivities.some(a => a.key === key);
                      return (
                        <button key={idx} onClick={() => handleCommonActivityToggle(activityName, 'pratique')}
                          className={`activity-btn practical ${isSelected ? 'selected' : ''}`}>
                          {isSelected && <Icon name="check" />} {activityName}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {activities.common?.length > 0 && (
                  <div className="activity-column">
                    <h4><Icon name="users" /> Communes</h4>
                    <div className="activity-list">
                      {activities.common.map((activityName, idx) => {
                        const key = `commun::${activityName}`;
                        const isSelected = selectedCommonActivities.some(a => a.key === key);
                        return (
                          <button key={idx} onClick={() => handleCommonActivityToggle(activityName, 'commun')}
                            className={`activity-btn common-btn ${isSelected ? 'selected' : ''}`}>
                            {isSelected && <Icon name="check" />} {activityName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="recurrence-selector">
                <h3>2. Récurrence — Appliquer aussi les jours :</h3>
                <div className="recurrence-days">
                  {getDates(session.startDate, session.endDate).map(date => (
                    <label
                      key={date}
                      className={`recurrence-day-item ${selectedRecurrenceDays.includes(date) ? 'checked' : ''} ${date === selectedDate ? 'current-day' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRecurrenceDays.includes(date)}
                        onChange={e => {
                          setSelectedRecurrenceDays(prev =>
                            e.target.checked ? [...prev, date] : prev.filter(d => d !== date)
                          );
                        }}
                      />
                      <span className="recurrence-day-label">
                        <span className="recurrence-day-short">{new Date(date).toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                        <span className="recurrence-day-num">{new Date(date).getDate()}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="activity-confirm-bar">
                {selectedCommonActivities.length > 0 && (
                  <span className="selected-count">
                    {selectedCommonActivities.length} activité{selectedCommonActivities.length > 1 ? 's' : ''} sélectionnée{selectedCommonActivities.length > 1 ? 's' : ''}
                    {selectedRecurrenceDays.length > 1 && <> &times; {selectedRecurrenceDays.length} jours</>}
                  </span>
                )}
                <button onClick={handleConfirmCommonActivities} className="btn-confirm-activities" disabled={selectedCommonActivities.length === 0}>
                  <Icon name="check" /> Confirmer{selectedRecurrenceDays.length > 1 ? ` (${selectedRecurrenceDays.length} jours)` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter un créneau horaire */}
      {showTimeSlotModal && (
        <div className="modal-overlay" onClick={() => { setShowTimeSlotModal(false); setNewTimeSlotInput(''); }}>
          <div className="modal-content timeslot-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajouter un créneau horaire</h2>
              <button onClick={() => { setShowTimeSlotModal(false); setNewTimeSlotInput(''); }} className="close-btn"><Icon name="close" /></button>
            </div>
            <div className="timeslot-form">
              <p className="timeslot-hint">Sélectionnez l'heure et les minutes (pas de 5 min)</p>
              <div className="timeslot-picker">
                <input
                  type="time"
                  value={newTimeSlotInput}
                  onChange={(e) => setNewTimeSlotInput(e.target.value)}
                  step="300"
                  className="timeslot-input"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTimeSlot(newTimeSlotInput)}
                />
                <button onClick={() => handleAddTimeSlot(newTimeSlotInput)} className="btn-submit" disabled={!newTimeSlotInput}>
                  <Icon name="plus" /> Ajouter
                </button>
              </div>
              {timeSlots.length > 0 && (
                <div className="timeslot-current">
                  <p className="timeslot-hint">Créneaux actuels (cliquez × sur le planning pour supprimer) :</p>
                  <div className="timeslot-quick-grid">
                    {timeSlots.map(h => (
                      <span key={h} className="timeslot-existing">{displayTime(h)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {saving && (
        <div className="saving-indicator">
          <Icon name="save" /> Sauvegarde en cours...
        </div>
      )}

      {ficheModalOpen && session && (
        <FichePalanquierModal
          session={session}
          selectedDate={selectedDate}
          initialData={editingFiche}
          onSave={handleSaveFiche}
          onClose={() => setFicheModalOpen(false)}
        />
      )}

      {showBlankFicheModal && (
        <div className="modal-overlay" onClick={() => setShowBlankFicheModal(false)}>
          <div className="modal-content" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Icon name="pdf" /> Fiche vierge</h2>
              <button onClick={() => setShowBlankFicheModal(false)} className="close-btn"><Icon name="close" /></button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.9rem' }}>Nombre de palanquées</label>
                  <input
                    type="number" min="1" max="20"
                    value={blankNbPal}
                    onChange={e => setBlankNbPal(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: '1rem', background: 'rgba(30,41,59,0.6)', color: '#e2e8f0' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, fontSize: '0.9rem' }}>Plongeurs par palanquée</label>
                  <input
                    type="number" min="1" max="12"
                    value={blankNbDiv}
                    onChange={e => setBlankNbDiv(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: '1rem', background: 'rgba(30,41,59,0.6)', color: '#e2e8f0' }}
                  />
                </div>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
                Génère un PDF avec {blankNbPal} palanquée{blankNbPal > 1 ? 's' : ''} de {blankNbDiv} ligne{blankNbDiv > 1 ? 's' : ''} vierge{blankNbDiv > 1 ? 's' : ''} chacune.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowBlankFicheModal(false)} className="btn-secondary">Annuler</button>
                <button onClick={handleGenerateBlankFiche} className="btn-primary"><Icon name="pdf" /> Générer le PDF</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier une activité */}
      {editingActivityGroup && (
        <div className="modal-overlay" onClick={() => { setEditingActivityGroup(null); setEditNewLevels([]); setEditNewActivities([]); }}>
          <div className="modal-content activity-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Icon name="edit" /> Modifier l'activité — {displayTime(editingActivityGroup.hour)}</h2>
              <button onClick={() => { setEditingActivityGroup(null); setEditNewLevels([]); setEditNewActivities([]); }} className="close-btn"><Icon name="close" /></button>
            </div>
            <div className="activity-selection">
              <div className="level-selector-group">
                <h3>1. Niveaux concernés</h3>
                <div className="level-checkboxes">
                  {levels.map(level => (
                    <label
                      key={level.id}
                      className={`level-checkbox-item ${editNewLevels.includes(level.id) ? 'checked' : ''}`}
                      style={{ borderColor: editNewLevels.includes(level.id) ? level.color : 'transparent', background: editNewLevels.includes(level.id) ? level.color + '22' : '' }}
                    >
                      <input
                        type="checkbox"
                        checked={editNewLevels.includes(level.id)}
                        onChange={e => setEditNewLevels(prev => e.target.checked ? [...prev, level.id] : prev.filter(id => id !== level.id))}
                      />
                      <span className="level-dot" style={{ background: level.color }}></span>
                      {level.name}
                    </label>
                  ))}
                </div>
              </div>
              <h3>2. Activités</h3>
              <div className="activity-columns">
                <div className="activity-column">
                  <h4><Icon name="clipboard" /> Théoriques</h4>
                  <div className="activity-list">
                    {activities.theoretical?.map((activityName, idx) => {
                      const key = `théorique::${activityName}`;
                      const isSelected = editNewActivities.some(a => a.key === key);
                      return (
                        <button key={idx} onClick={() => handleEditActivityToggle(activityName, 'théorique')}
                          className={`activity-btn theoretical ${isSelected ? 'selected' : ''}`}>
                          {isSelected && <Icon name="check" />} {activityName}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="activity-column">
                  <h4><Icon name="diving" /> Pratiques</h4>
                  <div className="activity-list">
                    {activities.practical?.map((activityName, idx) => {
                      const key = `pratique::${activityName}`;
                      const isSelected = editNewActivities.some(a => a.key === key);
                      return (
                        <button key={idx} onClick={() => handleEditActivityToggle(activityName, 'pratique')}
                          className={`activity-btn practical ${isSelected ? 'selected' : ''}`}>
                          {isSelected && <Icon name="check" />} {activityName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="activity-confirm-bar">
                <button onClick={handleConfirmEditActivity} className="btn-confirm-activities"
                  disabled={editNewLevels.length === 0 || editNewActivities.length === 0}>
                  <Icon name="check" /> Appliquer la modification
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Lien public */}
      {showPublicLinkModal && publicLinkToken && (
        <div className="modal-overlay" onClick={() => setShowPublicLinkModal(false)}>
          <div className="modal-content" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Icon name="link" /> Lien public du planning</h2>
              <button onClick={() => setShowPublicLinkModal(false)} className="close-btn"><Icon name="close" /></button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
                Ce lien permet de consulter le planning en lecture seule, sans connexion requise. Partagez-le avec vos participants.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 600 }}>LIEN SEMAINE COMPLÈTE</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    readOnly
                    value={`${window.location.origin}/public/${publicLinkToken}`}
                    style={{ flex: 1, padding: '0.5rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155', background: 'rgba(255,255,255,0.05)', color: '#e2e8f0', fontSize: '0.85rem' }}
                    onClick={e => e.target.select()}
                  />
                  <button className="btn-primary" style={{ whiteSpace: 'nowrap' }}
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/public/${publicLinkToken}`); toast.success('Lien copié !'); }}>
                    Copier
                  </button>
                </div>
              </div>
              <div style={{ borderTop: '1px solid #334155', paddingTop: '1rem' }}>
                <label style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>LIENS PAR JOUR</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 200, overflowY: 'auto' }}>
                  {getDates(session.startDate, session.endDate).map(date => (
                    <div key={date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.85rem', color: '#cbd5e1', minWidth: 120 }}>
                        {new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </span>
                      <button
                        style={{ fontSize: '0.78rem', padding: '0.25rem 0.75rem', borderRadius: 6, background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)', cursor: 'pointer' }}
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/public/${publicLinkToken}?day=${date}`); toast.success('Lien du jour copié !'); }}>
                        Copier lien du {new Date(date + 'T12:00:00').getDate()}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => setShowPublicLinkModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {copyingFiche && (
        <div className="modal-overlay" onClick={() => setCopyingFiche(null)}>
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Icon name="copy" /> Copier la fiche</h2>
              <button onClick={() => setCopyingFiche(null)} className="close-btn"><Icon name="close" /></button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="fiche-field">
                <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600 }}>Copier vers le jour :</label>
                <select
                  value={copyTargetDate}
                  onChange={e => setCopyTargetDate(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: '0.95rem' }}
                >
                  {getDates(session.startDate, session.endDate).map(d => (
                    <option key={d} value={d}>
                      {new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      {d === selectedDate ? ' (jour actuel)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setCopyingFiche(null)} className="btn-secondary">Annuler</button>
                <button onClick={handleConfirmCopyFiche} className="btn-primary"><Icon name="copy" /> Copier</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionPlanning;
